import React, { useState, useEffect, useRef, Suspense } from "react";
import { ChangeSet, groupObjectsFromDisjointedChanges, Location, mergeStateFromEvents, ObjectState, PrimitiveState, reconstructStateFromEvents, State, TrackedArray, TrackedPrimitive, TrackedRecord, TrackedObject, getCompressedEvents, Author } from "@aidkitorg/typesheets/lib/collab";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { safeParse, stringToColor } from "../Util";
import { MIGRATIONS } from "../Migrations";
import type { ChangesToRender } from "@aidkitorg/typesheets/lib/editor";
import { Button, CodeDisplay } from "@aidkitorg/component-library";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

const CollectionComponentKinds = new Set([
  'Section',
  'Collection',
  'Subsurvey',
  'Templated Block',
  'Payment',
  'Notification',
  'Usio Mailing',
  'GiveCard Mailing',
  'USBank Card Mailing',
  'Dashboard',
  'Applicant Identities',
  'Applicant Portal Page',
  'AI Assistant Review Survey',
  'Macro'
]);

const SectionComponentKinds = new Set([
  'Action',
  'Address',
  'Applicant Creator',
  'ArcGIS Attachment Viewer',
  'Assignee',
  'Attachment',
  'Bank Routing Number',
  'Benefits Calculator',
  'Card Management',
  'Consent',
  'Computed',
  'Confirmation',
  'Contact Confirmation',
  'Date',
  'Deck Verification',
  'Encrypted Value Verification',
  'Explanation',
  'Household Calculator',
  'Fillable Form',
  'Flag',
  'Duplicate Review',
  'Income Calculator',
  'Inline Language Selector',
  'Inline Notification',
  'Inline Signature',
  'Likert',
  'Liveness Detection',
  'Login Widget',
  'Lookup',
  'Number',
  'Number With Unit',
  'Plaid Bank Account',
  'Plaid Income',
  'Ranking Question',
  'Resume Widget',
  'Search Select',
  'Section',
  'Select',
  'Select From Data',
  'Show Field',
  'Similar Document Check',
  'Similar Document Review',
  'Submit Button',
  'Subsurvey',
  'Support Button',
  'Text Entry',
  'Third Party Check',
  'Validated',
  'Milestone',
  'Related Applicants',
  'Image Preview',
  'Conditional Block',
  'Templated Block'
]);

const OtherComponentKinds = new Set([
  'Persona',
  'Comms Config',
  'User',
  'User Group',
  'Fraud Flag',
  'Cross Program Data Exchange',
  'Geo Data Sync',
  'Root Tab'
]);

function RenderDistro(props: {
  object: TrackedObject | undefined,
  changes: {
    additions: {
      object: TrackedObject,
      location?: Location
    }[],
    removals: {
      object: TrackedObject,
      location?: Location
    }[]
  },
  parentIsRoot: boolean
}) {
  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const [events, setEvents] = useState<ChangeSet | undefined>();
  const [root, setRoot] = useState<TrackedObject | undefined>();
  const [rootKind, setRootKind] = useState<string | undefined>();
  const [initializedKey, setInitializedKey] = useState<string | undefined>();
  const [changesToRender, setChangesToRender] = useState<{
    additions: {
      object: TrackedObject,
      location?: Location,
      authors?: Author[]
    }[],
    removals: {
      object: TrackedObject,
      location?: Location,
      authors?: Author[]
    }[]
  }>(props.changes);

  if (!props.object) {
    return <div>Unknown object</div>
  }

  useEffect(() => {
    if (!props.object) return;

    // This keeps us from constantly re-creating the deepCopy.
    if (initializedKey === props.object.id + props.changes.additions.length + props.changes.removals.length) return;

    // need to create a distro state for this object only where the trackedObject IDs match the event ids from the actual state.
    // The tracked objects in this state need to be DeepCopies of the original objects so that we don't mess with the actual Distro if we click around.
    const workingSet: Record<string, TrackedObject> = {};
    const objects: Record<string,  ObjectState | PrimitiveState> = {};
    const state: State = {
      objects: objects,
      workingSet: workingSet,
      currentRevision: 0,
      changeSetOpen: 0,
      undoStack: [],
      redoStack: [],
    }

    // This helper takes a trackedObject and recursively creates and returns a deep copy of it.
    const deepCopyObject = (obj: TrackedObject): TrackedObject => {
      // Add the thing
      objects[obj.id] = obj.state.objects[obj.id];

      if (obj.state.objects[obj.id].kind === 'array' && Array.isArray(obj.value)) {
        workingSet[obj.id] = new TrackedArray(state, obj.id, obj.value.map(v => deepCopyObject(v)));
      } else if (obj.state.objects[obj.id].kind === 'record' && typeof obj.value === 'object') {
        workingSet[obj.id] = new TrackedRecord(state, obj.id, Object.keys(obj.value).reduce((acc, k) => {
          acc[k] = deepCopyObject(obj.value[k] as TrackedObject);
          return acc;
        }, {} as Record<string, TrackedObject>));
      } else {
        workingSet[obj.id] = new TrackedPrimitive(state, obj.id, obj.read()); 
      }

      return workingSet[obj.id];
    }

    let newObj = deepCopyObject(props.object);

    // These cover special cases that don't have a kind, like the users or program config tabs.
    if (props.parentIsRoot) {
      const newRoot = new TrackedRecord(state, 'root', {
        kind: new TrackedPrimitive(state, undefined, 'Root Tab'),
      });
      newRoot.set('changedObject', newObj);
      newObj = newRoot;
    }

    let events: ChangeSet | undefined = getCompressedEvents(newObj.state);
    let changesToRender = props.changes;

    // Distro is built to need a 'root' object - format the events so that our parent is always a root.
    if (newObj.id !== 'root') {
      events = events.map(event => {
        if (event[0] === newObj.id) {
          return ['root', event[1], event[2]]
        } else if (event[2]?.op === 'move' && event[2].loc?.id === newObj.id) {
          return [event[0], event[1], { ...event[2], loc: { ...event[2].loc, id: 'root' } }]
        }
        return event;
      })

      const renamedChanges = {
        additions: props.changes.additions.map(a => ({
          ...a,
          object: {
            ...a.object,
            id: a.object.id === newObj.id ? 'root' : a.object.id
          }
        } as {
          object: TrackedObject,
          location?: Location
        })),
        removals: props.changes.removals.map(r => ({
          ...r,
          object: {
            ...r.object,
            id: r.object.id === newObj.id ? 'root' : r.object.id
          }
        } as {
          object: TrackedObject,
          location?: Location
        })),
      }

      changesToRender = renamedChanges;
      newObj.id = 'root';
    }

    setRoot(newObj);
    setRootKind(newObj.read().kind);
    setEvents(events);
    setChangesToRender(changesToRender);
    setInitializedKey(props.object.id + props.changes.additions.length + props.changes.removals.length);
  }, [props, initializedKey]);

  // Initialize the distro in a separate useEffect that will do its logic after the deepCopy is done.
  // A lil hacky, but this gives the distro a chance to create the ref before we pass in the events / root that it will render with.
  useEffect(() => {
    if (!root || !events || !distroRef.current) return;

    distroRef.current?.initialize(root.read(), undefined, events, undefined, undefined, undefined, undefined, { changesToRender });
  }, [root, events, changesToRender]);

  if (rootKind && (CollectionComponentKinds.has(rootKind) || SectionComponentKinds.has(rootKind) || OtherComponentKinds.has(rootKind))) {
    return <div className="mt-4 bg-white md:rounded-lg">
      <Suspense>
        <Distro 
          ref={distroRef}
          types='src/survey.ts' 
          name={rootKind.replace(/\s+/g, '')}
          onChange={() => {}}
          migrations={MIGRATIONS}
        />
      </Suspense>
    </div>
  }

  return <div><div>Unable to render: {JSON.stringify(props.object?.read())}</div></div>
}

export function Changes(props: { 
  stateRef: React.MutableRefObject<State | undefined>,
  collabEnabled: boolean,
  surveyName: string
}) {
  const { stateRef: localStateRef, collabEnabled, surveyName } = props;
  const [numUnpublishedChanges, setNumUnpublishedChanges] = useState(0);
  const [changesToRender, setChangesToRender] = useState<Record<string, ChangesToRender>>({});

  const getLastPublishedEvents = usePost('/survey/get_last_published_events');
  const stateRef = useRef<State | undefined>(undefined);

  const [stateIsPrepared, setStateIsPrepared] = useState(false);

  const [expandedDistros, setExpandedDistros] = useState<Record<string, boolean>>({});

  // Helper to find the nearest parent that we know how to render.
  const findRenderableParent = (obj: TrackedObject | undefined): TrackedObject | undefined => {
    if (!obj) return undefined;
    const kind = obj?.read().kind;
    if (kind && (CollectionComponentKinds.has(kind) || SectionComponentKinds.has(kind) || OtherComponentKinds.has(kind))) {
      return obj;
    }

    // root is a renderable parent via the Root Tab.
    if (obj.id === 'root') {
      return obj;
    }

    return findRenderableParent(obj.parent);
  }

  useAsyncEffect(async () => {
    stateRef.current = localStateRef.current;

    if (!collabEnabled) {
      setStateIsPrepared(true);
      return;
    }

    const { savedVersionURL } = await getLastPublishedEvents({ name: surveyName });
    if (savedVersionURL) {
      const response = await fetch(savedVersionURL);
      const events = safeParse(await response.text(), []) as ChangeSet;

      // Collab mode is automatically pruning and auto-saving as changes are made.
      // We need to merge the unpublished events into the published version so we have a complete picture of what has happened.
      const [_, state] = reconstructStateFromEvents([...events], false);

      const [__, replayedState] = mergeStateFromEvents(state, localStateRef.current?.unpublishedChanges || []);
      stateRef.current = replayedState;
    }

    setStateIsPrepared(true);
  }, [collabEnabled, numUnpublishedChanges]);

  useEffect(() => {
    setNumUnpublishedChanges(localStateRef.current?.unpublishedChanges?.length || 0);
  }, [localStateRef.current, surveyName, collabEnabled])

  // This useEffect does all the work of figuring out what to render.
  // We don't want it to happen over and over again whenever the stateRef changes, so we have a dependency on numUnpublishedChanges.
  useEffect(() => {
    if (!stateIsPrepared) return;

    // First, group the events into disjointed TrackedObjects that represent the changes.
    const groupedChanges = (groupObjectsFromDisjointedChanges(localStateRef.current?.unpublishedChanges || []));

    // Next, we figure out which of these grouped changes are "relevant". 
    // Some changes, for example, are objects getting added and then immediately removed. This happens a lot - 
    // when someone adds a Section, then realizes that they want a Subsurvey. That is a Section added and then deleted.
    // This change is not relevant, and we do not want to show it.
    const relevantChanges: Record<string, { object: TrackedObject, events: ChangeSet }> = {};
    for (const objId of Object.keys(groupedChanges)) {
      const objInSurvey = stateRef.current?.workingSet[objId];
  
      // no parent = removal.
      if (!objInSurvey?.parent) {
        const allObjMoves = stateRef.current?.objects[objId]?.locations || [];

        const unpublishedObjMoves = groupedChanges[objId].events.filter(event => event[2].op === 'move').map(event => event[2]);
        let hasPreExistingLocation = false;
  
        for (const move of allObjMoves) {
          const unpublishedMove = unpublishedObjMoves.find(m => m.id === move.id);
  
          // if an object move does not exist in the unpublished changes, it means it was added outside of these changes.
          // so we DO care about its removal.
          if (!unpublishedMove && !!move.loc) {
            hasPreExistingLocation = true;
            break;
          }
        }
  
        if (hasPreExistingLocation) {
          relevantChanges[objId] = {
            object: stateRef.current?.workingSet[objId] as TrackedObject,
            events: groupedChanges[objId].events
          };
        }
      } else {
        // if it has a parent, it does still exist in the survey, so these changes are relevant.
        relevantChanges[objId] = {
          object: stateRef.current?.workingSet[objId] as TrackedObject,
          events: groupedChanges[objId].events
        }
      }
    }

    // Now we get our changes ready to render.
    const changesToRender = {} as Record<string, ChangesToRender>;
    for (const objId of Object.keys(relevantChanges)) {
      const lastLoc = (stateRef.current?.objects[objId]?.locations.filter(l => !!l.loc).reduce((a, b) => (a.prev_gen > b.prev_gen) ? a : b) || undefined)?.loc;
      const uniqueAuthors: Record<string, Author> = {};
      for (const event of relevantChanges[objId].events) {
        const author = event[2].author;
        if (author) {
          uniqueAuthors[author.name + '/' + author.tabId] = author;
        }
      }
      const authors = Object.values(uniqueAuthors);

      let parentToRender = findRenderableParent(stateRef.current?.workingSet[objId]);
      if (parentToRender) {
        const changeId = parentToRender.id === 'root' ? `root-${objId}` : parentToRender.id;
        // if the parent is root, group it separately from other root children. We don't want to render the whole root at once.
        changesToRender[changeId] ||= {
          additions: [],
          removals: []
        } as ChangesToRender;
  
        if (stateRef.current?.workingSet[objId]?.parent) {
          changesToRender[changeId].additions.push({
            object: relevantChanges[objId].object,
            location: lastLoc,
            authors: authors
          });
        } else {
          changesToRender[changeId].removals.push({
            object: relevantChanges[objId].object,
            location: lastLoc,
            authors: authors
          });
        }
        continue;
      }
  
      // If we haven't found how to place the thing, it means its been removed and we need to look at previous parents.
      if (!stateRef.current?.workingSet[objId]?.parent) {
        const prevLocations = (stateRef.current?.objects[objId]?.locations || []);
        const mostRecentLocation = prevLocations.filter(l => !!l.loc).reduce((a, b) => (a.prev_gen > b.prev_gen) ? a : b);
        const lastParent = mostRecentLocation.loc?.id ? stateRef.current?.workingSet[mostRecentLocation.loc?.id] : undefined;
        if (lastParent) {
          // the last parent might not be a renderable parent, so we need to find the nearest renderable parent.
          parentToRender = findRenderableParent(lastParent);
          if (parentToRender) {
            changesToRender[parentToRender.id === 'root' ? `root-${objId}` : parentToRender.id] ||= {
              additions: [] as {
                object: TrackedObject,
                location?: Location,
                authors?: Author[]
              }[],
              // add the change as a removal
              removals: [{
                object: relevantChanges[objId].object,
                location: lastLoc,
                authors: authors
              }] as {
                object: TrackedObject,
                location?: Location,
                authors?: Author[]
              }[]
            };
            continue;
          }
        }
      }
  
      // This means we do not know how to render the object. 
      // We would love to get to a place where nothing falls in this category.
      changesToRender[objId] ||= {
        additions: [] as {
          object: TrackedObject,
          location?: Location,
          authors?: Author[]
        }[],
        removals: [] as {
          object: TrackedObject,
          location?: Location,
          authors?: Author[]
        }[]
      };
  
      if (stateRef.current?.workingSet[objId]?.parent) {
        changesToRender[objId].additions.push({
          object: relevantChanges[objId].object,
          location: lastLoc,
          authors: authors
        }); 
      } else {
        changesToRender[objId].removals.push({
          object: relevantChanges[objId].object,
          location: lastLoc,
          authors: authors
        });
      }
    }

    setChangesToRender(changesToRender);
  }, [numUnpublishedChanges, stateIsPrepared])

  return <div className="bg-gray-50 min-h-screen">
    <div className="text-lg font-bold">Current Unpublished Changes: {!numUnpublishedChanges ? 'None!' : ''}</div>
    <ul role="list">
      {
        Object.keys(changesToRender).map((id) => {
          const objId = id.startsWith('root-') ? id.split('-')[1] : id;
          const isRoot = id.startsWith('root-');

          return (
            <li className="p-2 m-2 bg-white rounded shadow" key={objId}>
              <div className="flex justify-between px-1">
                <div>
                  Modified object: 
                  <span className="rounded-full text-sm px-2 py-1 inline-block mx-1" style={{ "backgroundColor": stringToColor(stateRef.current?.workingSet[objId]?.value?.kind?.value || (isRoot ? 'root' : objId), '70%') }}>
                    {stateRef.current?.workingSet[objId]?.value?.kind?.value || (isRoot ? 'root' : objId)}
                  </span>
                </div>
                <Button size="sm" variant="secondary" onClick={() => {
                  setExpandedDistros({
                    ...expandedDistros,
                    [objId]: !expandedDistros[objId]
                  });
                }}>{expandedDistros[objId] ? 'Collapse Distro' : 'Expand as Distro'}
                </Button>
              </div>
              <div className="mx-4 mt-2">
                {changesToRender[id].additions.map((addition) => {
                  const readObject = addition.object.read();
                  return <div key={addition.object.id}><CodeDisplay className="text-sm px-2 py-1 bg-green-100 text-black rounded whitespace-pre-wrap break-words block"
                    code={JSON.stringify(readObject, null, 2)}
                    language="json"
                    showLineNumbers={false}
                    copyable={false}
                    expandable={true}
                  />
                  {addition.authors && <div className="text-xs mt-1 flex justify-end">
                    {addition.authors.length > 1 ? 'Authors: ' : 'Author: '} {addition.authors.map(author => author.name).join(', ')}
                  </div>}
                  </div>
                })}
                {changesToRender[id].removals.map((removal) => {
                  const readObject = removal.object.read();
                  return <div key={removal.object.id}><CodeDisplay className="text-sm px-2 py-1 bg-red-100 text-black rounded whitespace-pre-wrap break-words block"
                    code={JSON.stringify(readObject, null, 2)}
                    language="json"
                    showLineNumbers={false}
                    copyable={false}
                    expandable={true}
                  />
                  {removal.authors && <div className="text-xs mt-1 flex justify-end">
                    {removal.authors.length > 1 ? 'Authors: ' : 'Author: '} {removal.authors.map(author => author.name).join(', ')}
                  </div>}
                  </div>
                })}
              </div>
              
              {expandedDistros[objId] && <RenderDistro object={stateRef.current?.workingSet[objId]} changes={changesToRender[id]} parentIsRoot={id.startsWith('root-')} />}
            </li>
          )
        })
      }
    </ul>
  </div>
}
